/**
 * BranchTransferAlertBubble
 *
 * Floating alert bubble for branch transfer notifications.
 * Unlike the PriceScanManager bubble, snoozing FULLY HIDES the bubble
 * until the snooze timer expires (it does not stay in a bell/notification tray).
 *
 * Alert rows (one per lifecycle stage where stock/money hangs):
 *  approvals · stock requests to triage (/stock-requests) · in transit ·
 *  variance review · disputed re-counts · returned drafts
 *
 * Max snooze: 1 hour. Stored in localStorage.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Package, ArrowRight, ChevronDown, X, Clock, Bell } from 'lucide-react';

const POLL_INTERVAL_MS = 60_000; // 1 minute
const SNOOZE_OPTIONS = [
  { label: '15 minutes', ms: 15 * 60 * 1000 },
  { label: '30 minutes', ms: 30 * 60 * 1000 },
  { label: '1 hour', ms: 60 * 60 * 1000 },
];
const SNOOZE_KEY = 'bt_alert_snoozed_until';

function getSnoozedUntil() {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    return raw ? parseInt(raw, 10) : 0;
  } catch { return 0; }
}

function setSnoozedUntil(ms) {
  try {
    localStorage.setItem(SNOOZE_KEY, String(ms));
  } catch {}
}

export default function BranchTransferAlertBubble() {
  const { user, currentBranch, hasPerm } = useAuth();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const [counts, setCounts] = useState({ pending_approvals: 0, new_flow_requests: 0, total: 0 });
  const [visible, setVisible] = useState(false);
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(false);
  const [dismissed, setDismissed] = useState(false); // session-level close without snooze

  const role = user?.role;
  const canSee = role === 'admin' || role === 'manager' ||
    (user?.permissions?.branch_transfers?.approve === true);

  const fetchAlerts = useCallback(async () => {
    if (!canSee) return;
    // Check snooze — if snoozed, keep hidden
    if (getSnoozedUntil() > Date.now()) {
      setVisible(false);
      return;
    }
    try {
      const params = {};
      if (currentBranch?.id) params.branch_id = currentBranch.id;
      const res = await api.get('/branch-transfers/pending-alerts', { params });
      const data = res.data || {};
      setCounts(data);
      setVisible((data.total || 0) > 0 && !dismissed);
    } catch {
      // silently fail
    }
  }, [canSee, currentBranch?.id, dismissed]);

  // Initial fetch + polling
  useEffect(() => {
    fetchAlerts();
    const id = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchAlerts]);

  // Instant hand-off (owner 2026-09-03): a triage / transfer action fires
  // this event so the float reflects it immediately — the request row goes,
  // the transfer's row appears — instead of waiting for the next poll.
  useEffect(() => {
    const onRefresh = () => fetchAlerts();
    window.addEventListener('agribooks:alerts-refresh', onRefresh);
    return () => window.removeEventListener('agribooks:alerts-refresh', onRefresh);
  }, [fetchAlerts]);

  // Re-check snooze expiry every 30s so bubble re-appears after snooze ends
  useEffect(() => {
    const id = setInterval(() => {
      if (getSnoozedUntil() <= Date.now()) {
        setDismissed(false);
        fetchAlerts();
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [fetchAlerts]);

  // Close snooze menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowSnoozeMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSnooze = (ms) => {
    setSnoozedUntil(Date.now() + ms);
    setVisible(false);
    setShowSnoozeMenu(false);
  };

  const handleDismiss = () => {
    // Session dismiss — bubble hides until next poll finds new items or page refresh
    setDismissed(true);
    setVisible(false);
  };

  // One deep-link per bucket (audit BT-PILL-01/SRBT-01): every row lands on
  // the exact screen that clears it — new-flow requests live on /stock-requests.
  const NAV_BY_TYPE = {
    approvals: '/branch-transfers?tab=history&subtab=pending_approval',
    new_flow: '/stock-requests',
    incoming_sent: '/branch-transfers?tab=history&subtab=sent',
    variance_review: '/branch-transfers?tab=history&subtab=received_pending',
    disputed: '/branch-transfers?tab=history&subtab=disputed',
    returned: '/branch-transfers?tab=history&subtab=returned',
  };
  const handleNavigate = (type) => {
    navigate(NAV_BY_TYPE[type] || '/branch-transfers');
    setVisible(false);
  };

  if (!visible || !canSee) return null;

  return (
    <div
      className="fixed bottom-24 right-4 z-50 w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="bt-alert-bubble"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#1A4D2E] text-white rounded-t-2xl">
        <div className="flex items-center gap-2">
          <Bell size={14} className="opacity-80" />
          <span className="text-sm font-semibold">Branch Transfer Alerts</span>
          <span className="text-xs bg-white/20 rounded-full px-2 py-0.5 font-bold">
            {counts.total}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Snooze dropdown — opens downward so it's never clipped by viewport */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowSnoozeMenu(v => !v)}
              className="flex items-center gap-1 text-xs text-white/80 hover:text-white px-2 py-1 rounded hover:bg-white/10 transition-colors"
              data-testid="bt-bubble-snooze-btn"
            >
              <Clock size={12} />
              Snooze
              <ChevronDown size={11} className={`transition-transform ${showSnoozeMenu ? 'rotate-180' : ''}`} />
            </button>
            {showSnoozeMenu && (
              <div className="absolute right-0 top-full mt-1 z-[100] bg-white border border-slate-200 rounded-lg shadow-xl w-44 py-1">
                <p className="text-[10px] text-slate-400 px-3 py-1.5 font-medium uppercase border-b">
                  Hide bubble for...
                </p>
                {SNOOZE_OPTIONS.map(opt => (
                  <button
                    key={opt.ms}
                    onClick={() => handleSnooze(opt.ms)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                    data-testid={`bt-snooze-${opt.ms}`}
                  >
                    <Clock size={12} className="text-slate-400" />
                    {opt.label}
                  </button>
                ))}
                <p className="text-[9px] text-slate-400 px-3 py-1.5 border-t mt-1">
                  Bubble fully disappears until timer ends.
                </p>
              </div>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/70 hover:text-white p-1 rounded hover:bg-white/10 transition-colors"
            data-testid="bt-bubble-close-btn"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Alert rows */}
      <div className="divide-y divide-slate-100">
        {counts.pending_approvals > 0 && (
          <button
            onClick={() => handleNavigate('approvals')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-amber-50 transition-colors text-left"
            data-testid="bt-bubble-approvals"
          >
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Package size={14} className="text-amber-700" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {counts.pending_approvals} awaiting approval
                </p>
                <p className="text-xs text-slate-500">Transfer requests need your sign-off</p>
              </div>
            </div>
            <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
          </button>
        )}

        {(counts.new_flow_requests || 0) > 0 && (
          <button onClick={() => handleNavigate('new_flow')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-violet-50 transition-colors text-left"
            data-testid="bt-bubble-new-flow">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                <Package size={14} className="text-violet-700" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {counts.new_flow_requests} stock {counts.new_flow_requests === 1 ? 'request' : 'requests'} to triage
                </p>
                <p className="text-xs text-slate-500">A branch is waiting on your stock</p>
              </div>
            </div>
            <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
          </button>
        )}

        {(counts.incoming_sent || 0) > 0 && (
          <button onClick={() => handleNavigate('incoming_sent')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-50 transition-colors text-left"
            data-testid="bt-bubble-incoming-sent">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Package size={14} className="text-blue-700" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {counts.incoming_sent} in transit to you
                </p>
                <p className="text-xs text-slate-500">Receive when the goods arrive</p>
              </div>
            </div>
            <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
          </button>
        )}

        {(counts.variance_review || 0) > 0 && (
          <button onClick={() => handleNavigate('variance_review')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-red-50 transition-colors text-left"
            data-testid="bt-bubble-variance">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Package size={14} className="text-red-700" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {counts.variance_review} variance to review
                </p>
                <p className="text-xs text-slate-500">Destination counted a difference — accept or dispute</p>
              </div>
            </div>
            <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
          </button>
        )}

        {(counts.disputed_recounts || 0) > 0 && (
          <button onClick={() => handleNavigate('disputed')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-orange-50 transition-colors text-left"
            data-testid="bt-bubble-disputed">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Package size={14} className="text-orange-700" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {counts.disputed_recounts} re-count requested
                </p>
                <p className="text-xs text-slate-500">Source disputed your count — count again</p>
              </div>
            </div>
            <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
          </button>
        )}

        {(counts.returned_drafts || 0) > 0 && (
          <button onClick={() => handleNavigate('returned')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-100 transition-colors text-left"
            data-testid="bt-bubble-returned">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                <Package size={14} className="text-slate-600" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {counts.returned_drafts} bounced back to you
                </p>
                <p className="text-xs text-slate-500">Rejected transfer — fix and resubmit</p>
              </div>
            </div>
            <ArrowRight size={14} className="text-slate-400 flex-shrink-0" />
          </button>
        )}

      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 rounded-b-2xl">
        <p className="text-[10px] text-slate-400">
          Click a row to go directly to the relevant section.
        </p>
      </div>
    </div>
  );
}
